// src/services/sourceQuestionTemplates.js
//
// SourceX — reusable question blocks ("Use from template").
//
// Some questions recur, worded identically, across many studies' source
// documents — the standing "any new con-meds / AEs / exacerbations since last
// visit?" prompts. Rather than retype them per visit, a coordinator picks one
// here and it is inserted into the current visit as ordinary, fully-editable
// questions (heading + fields), exactly like anything they'd add by hand.
//
// This file is DATA ONLY. Each template lists a dropdown `label` (the heading)
// and the `questions` to insert. They are normalized through makeQuestion at
// insert time (see SectionEditor.insertTemplate), so every inserted field gets
// a fresh id and the canonical v2 shape — nothing here is a live question, and
// nothing here writes to a template or to patient data.

// THE STANDING LOGS LIVE ELSEWHERE NOW
//
// The Con Med, AE and Exacerbation logs used to sit here, withdrawn behind
// `unavailable` while they were revised. They have been rewritten and moved to
// `sourceLogTemplates.js`, reached from the template page's "Add Log" button.
//
// They moved because a standing log is not a visit's question: it runs for the
// whole study, so it belongs in its own section printing its own landscape
// page with a blank "Page __ of __", not appended to whichever visit happened
// to be selected. Inserting one here was always the wrong home for it.
//
// TEMPORARILY WITHDRAWN BLOCKS
//
// A block carrying `unavailable` is still listed, greyed out and un-pickable,
// with that string as the reason. It is deliberately not deleted: the content
// is the starting point for the revision, and deleting it would mean rebuilding
// it from scratch to bring it back. Re-enabling one is deleting its
// `unavailable` line and nothing else.
//
// The flag lives HERE, in the data, rather than being inferred in the UI from
// something like "the label contains Log" — which block is withdrawn is a
// deliberate decision, not a pattern.

/**
 * @typedef {object} SourceQuestionTemplate
 * @property {string} id     stable key for the dropdown
 * @property {string} [unavailable]  why this block can't be used right now;
 *   absent means it's available
 * @property {string} label  heading text — what the dropdown shows, and the
 *                           Header that gets inserted above the questions
 * @property {Array<object>} questions  makeQuestion-shaped seeds (no ids)
 */

// Yes/No printed as a checkbox pair on the paper forms. A Select with exactly
// these two options prints as two option boxes, which is what the source
// document shows — "YesNo/Maybe" would add an N/A the forms don't offer.
const YES_NO = ["Yes", "No"];

// Physical examination body systems, in the fixed printed order. The paper form
// pre-prints these as row labels; a Table can't carry pre-filled rows (the PHI
// guard strips every value on write), so each system is its own question.
const PE_SYSTEMS = [
  "General Appearance",
  "Skin",
  "Head, Eyes, Ears, Nose, Throat, Mouth",
  "Neck (including thyroid)",
  "Respiratory",
  "Abdomen",
  "Extremities",
  "Neurologic/CNS",
  "Cardiovascular (include bruits + pulses)",
  "Musculoskeletal",
  "Lymph Nodes",
  "Other, specify",
];

/** @type {SourceQuestionTemplate[]} */
export const SOURCE_QUESTION_TEMPLATES = [
  {
    id: "concomitant-medications",
    label: "Concomitant Medications",
    questions: [
      {
        type: "Select",
        question:
          "Were there any new concomitant medications or changes to previously reported concomitant medications since the last study visit that have not yet been documented?",
        options: [
          "No",
          "Yes — add all new or changed medications to the Concomitant Medication Log.",
        ],
      },
    ],
  },
  {
    id: "adverse-events",
    label: "Adverse Events",
    questions: [
      {
        type: "Select",
        question:
          "Were there any new adverse events or changes to previously reported adverse events since the last study visit that have not yet been documented?",
        options: [
          "No",
          "Yes — add all new or updated adverse events to the Adverse Event Log.",
        ],
      },
    ],
  },
  {
    id: "exacerbations",
    label: "Exacerbations",
    questions: [
      {
        type: "Select",
        question:
          "Were there any new exacerbations or changes to previously reported exacerbations since the last study visit that have not yet been documented?",
        options: [
          "No",
          "Yes — add all new or updated exacerbations to the Exacerbation Log.",
        ],
      },
    ],
  },

  // -------------------------------------------------------------------------
  // A baseline-history grid that belongs INSIDE a visit — it records what was
  // true before the study started, so it is asked once, at the visit that asks
  // it, and is not a standing log. (The standing logs moved to
  // sourceLogTemplates.js; see the note at the top of this file.) It mirrors
  // one printed CRF page: the log as a repeatable Table whose `minRows`
  // matches the blank rows the page prints.
  // -------------------------------------------------------------------------
  {
    id: "hospitalization-log",
    unavailable: "These logs are being revised — not for use yet.",
    label: "Hospitalization Log",
    questions: [
      {
        type: "Table",
        // The printed instruction line sits above the grid; carried in the
        // label so it survives without inventing a field to hold it.
        question:
          "Hospitalizations — include ALL hospitalizations in the 12 months prior to the baseline visit",
        minRows: 13,
        columns: [
          { label: "Reason / Diagnosis", type: "Text" },
          {
            label: "Related to Asthma Exacerbation?",
            type: "Select",
            options: YES_NO,
          },
          { label: "Days in Intensive Care", type: "Number" },
          { label: "ICD-10 Code(s)", type: "Text" },
          {
            label: "Required Intubation or Ventilation?",
            type: "Select",
            options: YES_NO,
          },
          { label: "If yes, specifics", type: "Text" },
          { label: "Admission Date", type: "Date" },
          { label: "Prior to Study Start?", type: "Select", options: YES_NO },
          { label: "Discharge Date", type: "Date" },
        ],
      },
    ],
  },
  {
    id: "physical-exam",
    label: "Physical Exam",
    questions: [
      {
        type: "Table",
        question: "Physical Examination",
        // Fixed rows, one per body system, pre-printed exactly as on the paper
        // form. rowLabels is structure rather than an answer, so it survives
        // the PHI guard that blanks every value on a template write.
        rowLabelHeader: "SYSTEM",
        rowLabels: PE_SYSTEMS,
        columns: [
          { label: "Not Done", type: "Checkbox" },
          { label: "Normal", type: "Checkbox" },
          { label: "Abnormal — specify", type: "Text" },
          // Printed as a ☐ Yes ☐ No pair on the form. MultiSelect is what
          // renders actual tick boxes on screen — a Select would show a
          // dropdown, though both print as boxes in the export.
          { label: "Clinically Significant", type: "MultiSelect", options: YES_NO },
        ],
      },
      { type: "TextArea", question: "Additional comments" },
      {
        type: "Signature",
        question: "Signature of Examiner",
        signatureRole: "Examiner",
      },
    ],
  },
];

/**
 * The same blocks, ordered for the "Use from template" MENU: everything
 * available first, everything withdrawn after it.
 *
 * A withdrawn block can't be picked or dragged, so sitting mid-list it was a
 * dead row between two live ones — read, reached for, and only then found to be
 * greyed out. At the bottom it is still visible (which is the point of listing
 * it at all) without interrupting the list of things that actually work.
 *
 * A separate export rather than a re-ordering of the array above, because that
 * order is the DATA's own — the visit blocks together, then the ones that
 * mirror a printed CRF page — and lookups by id read from it. Both filters are
 * stable, so blocks keep their relative order within each half.
 */
export const SOURCE_QUESTION_TEMPLATES_MENU = [
  ...SOURCE_QUESTION_TEMPLATES.filter((t) => !t.unavailable),
  ...SOURCE_QUESTION_TEMPLATES.filter((t) => t.unavailable),
];
